import { useState } from "react";
import { Sparkles, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import GetStartedButton from "@/components/landing/GetStartedButton";
import aelyraImg from "@/assets/instructor-aelyra.png";
import caelionImg from "@/assets/instructor-caelion.png";
import vaelorImg from "@/assets/instructor-vaelor.png";

const lumiImg = "/assets/lumi-vanta.png";

type Guide = {
  id: string;
  src: string;
  name: string;
  fullName: string;
  role: string;
  line: string;
  ring: string;
  glow: string;
  accent: string;
  pos: string; // tailwind positioning around the central portal
};

const guides: Guide[] = [
  {
    id: "aelyra",
    src: aelyraImg,
    name: "Aelyra",
    fullName: "Aelyra Solari",
    role: "Finance Guide",
    line: "Cash flow, saving, and the math of real money.",
    ring: "ring-cyan-300/45",
    glow: "shadow-[0_0_60px_-10px_rgba(34,211,238,0.6)]",
    accent: "text-cyan-200/90",
    pos: "top-2 left-1/2 -translate-x-1/2",
  },
  {
    id: "vaelor",
    src: vaelorImg,
    name: "Vaelor",
    fullName: "Headmaster Vaelor",
    role: "Entrepreneurship Guide",
    line: "Building, ownership, and thinking like a founder.",
    ring: "ring-amber-300/45",
    glow: "shadow-[0_0_70px_-10px_rgba(251,191,36,0.6)]",
    accent: "text-amber-200/90",
    pos: "bottom-6 left-4",
  },
  {
    id: "caelion",
    src: caelionImg,
    name: "Caelion",
    fullName: "Caelion Veyr",
    role: "Economics Guide",
    line: "Systems, incentives, and tradeoffs made simple.",
    ring: "ring-violet-300/45",
    glow: "shadow-[0_0_60px_-10px_rgba(167,139,250,0.6)]",
    accent: "text-violet-200/90",
    pos: "bottom-6 right-4",
  },
];

const lumi: Guide = {
  id: "lumi",
  src: lumiImg,
  name: "Lumi",
  fullName: "Lumi Vanta",
  role: "AI Companion",
  line: "Coaching, prompting, and helping teens keep moving.",
  ring: "ring-indigo-300/55",
  glow: "shadow-[0_0_80px_-10px_rgba(129,140,248,0.7)]",
  accent: "text-indigo-200/95",
  pos: "",
};

const HeroSection = () => {
  const [activeGuide, setActiveGuide] = useState<string | null>(null);

  const scrollToExperience = () => {
    document.getElementById("learning-experience")?.scrollIntoView({ behavior: "smooth" });
  };

  const renderGuideTooltip = (g: Guide, placement: "top" | "bottom" | "left" | "right") => {
    const isActive = activeGuide === g.id;
    const posClass =
      placement === "top"
        ? "left-1/2 -translate-x-1/2 -translate-y-2 bottom-full mb-3"
        : placement === "bottom"
        ? "left-1/2 -translate-x-1/2 translate-y-2 top-full mt-3"
        : placement === "left"
        ? "right-full mr-3 top-1/2 -translate-y-1/2"
        : "left-full ml-3 top-1/2 -translate-y-1/2";

    return (
      <div
        role="tooltip"
        className={`pointer-events-none absolute z-40 w-[200px] rounded-2xl border border-white/15 bg-[#11102a]/95 backdrop-blur-xl p-3.5 shadow-[0_24px_70px_-20px_rgba(0,0,0,0.8)] transition-all duration-200 ${posClass} ${
          isActive ? "opacity-100 translate-y-0 scale-100" : "opacity-0 scale-95"
        }`}
      >
        <div className={`text-[9px] uppercase tracking-[0.3em] ${g.accent}`}>{g.role}</div>
        <div className="mt-1 font-heading text-sm text-white">{g.fullName}</div>
        <p className="mt-1.5 text-[11px] text-white/70 leading-snug">{g.line}</p>
      </div>
    );
  };

  const renderGuideButton = (
    g: Guide,
    sizeCls: string,
    tooltipPlacement: "top" | "bottom" | "left" | "right",
    extraRingCls = "",
  ) => (
    <button
      type="button"
      onMouseEnter={() => setActiveGuide(g.id)}
      onMouseLeave={() => setActiveGuide((cur) => (cur === g.id ? null : cur))}
      onFocus={() => setActiveGuide(g.id)}
      onBlur={() => setActiveGuide((cur) => (cur === g.id ? null : cur))}
      onClick={() => setActiveGuide((cur) => (cur === g.id ? null : g.id))}
      aria-label={`${g.fullName}, ${g.role}. ${g.line}`}
      aria-expanded={activeGuide === g.id}
      className={`group relative rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0c0a1f] transition-transform duration-300 hover:scale-[1.06] ${
        activeGuide === g.id ? "scale-[1.06]" : ""
      }`}
    >
      <span
        aria-hidden
        className={`absolute -inset-2 rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.18),transparent_65%)] opacity-0 group-hover:opacity-100 ${
          activeGuide === g.id ? "opacity-100" : ""
        } transition-opacity`}
      />
      <span className={`relative block ${sizeCls} rounded-full ring-2 ${g.ring} overflow-hidden bg-[#0e0d24] ${g.glow} ${extraRingCls}`}>
        <img src={g.src} alt="" className="h-full w-full object-cover" />
      </span>
      {renderGuideTooltip(g, tooltipPlacement)}
    </button>
  );

  return (
    <section className="relative pt-28 sm:pt-32 pb-16 sm:pb-24 overflow-hidden">
      {/* Hero ambient: rotating portal + soft washes */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 right-[-10%] h-[680px] w-[680px] rounded-full bg-[conic-gradient(from_120deg,rgba(34,211,238,0.18),rgba(99,102,241,0.18),rgba(167,139,250,0.16),rgba(251,191,36,0.12),rgba(34,211,238,0.18))] blur-3xl opacity-70 animate-[spin_60s_linear_infinite]" />
        <div className="absolute -bottom-40 -left-20 h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,rgba(99,102,241,0.35),transparent_60%)] blur-3xl" />
      </div>

      <div className="container mx-auto px-6 max-w-6xl relative">
        <div className="grid lg:grid-cols-[1.02fr_0.98fr] gap-10 lg:gap-14 items-center">
          {/* Left: copy */}
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 mb-6 backdrop-blur-md">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.95)] animate-pulse" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-white/85">
                HeirWise · Economic & Enterprise Education
              </span>
            </div>

            <h1 className="font-heading text-[2.5rem] sm:text-5xl lg:text-[3.4rem] text-white leading-[1.04] tracking-tight">
              Help your teen practice economic and enterprise decisions{" "}
              <span className="bg-gradient-to-r from-cyan-200 via-indigo-200 to-violet-200 bg-clip-text text-transparent">
                before real life makes them expensive.
              </span>
            </h1>

            <p className="mt-6 text-base sm:text-lg text-white/75 max-w-xl leading-relaxed">
              HeirWise helps teens build practical judgment across money, economics, business, and entrepreneurship. Teens practice realistic decisions while parents get progress insight without micromanaging.
            </p>

            <p className="mt-3 text-sm text-white/55 max-w-xl">
              Guided by the <span className="text-white/80">Lunimar Collective</span> — instructors and Lumi help teens learn, decide, reflect, and keep moving.
            </p>

            {/* Mobile guide chip rail (always visible on small screens since the orbit is hidden) */}
            <div className="mt-6 lg:hidden">
              <div className="text-[10px] uppercase tracking-[0.28em] text-white/45 mb-3">Your guides</div>
              <div className="flex flex-wrap items-start gap-3">
                {[...guides, lumi].map((g) => {
                  const isActive = activeGuide === g.id;
                  return (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => setActiveGuide(isActive ? null : g.id)}
                      aria-expanded={isActive}
                      aria-label={`${g.fullName}, ${g.role}`}
                      className={`flex items-center gap-2 rounded-full pl-1 pr-3 py-1 border ${
                        isActive ? "border-white/30 bg-white/[0.08]" : "border-white/10 bg-white/[0.04]"
                      } backdrop-blur-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70`}
                    >
                      <span className={`relative block h-8 w-8 rounded-full ring-2 ${g.ring} overflow-hidden bg-[#0e0d24]`}>
                        <img src={g.src} alt="" className="h-full w-full object-cover" />
                      </span>
                      <span className="text-[11px] text-white/85">{g.name}</span>
                    </button>
                  );
                })}
              </div>
              {activeGuide && (
                <div
                  role="region"
                  aria-live="polite"
                  className="mt-3 rounded-2xl border border-white/15 bg-[#11102a]/85 backdrop-blur-xl p-4"
                >
                  {(() => {
                    const g = [...guides, lumi].find((x) => x.id === activeGuide)!;
                    return (
                      <>
                        <div className={`text-[10px] uppercase tracking-[0.3em] ${g.accent}`}>{g.role}</div>
                        <div className="mt-1 font-heading text-sm text-white">{g.fullName}</div>
                        <p className="mt-1.5 text-xs text-white/70 leading-snug">{g.line}</p>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <GetStartedButton
                size="lg"
                className="relative h-12 px-7 bg-gradient-to-r from-cyan-400 via-indigo-500 to-violet-500 text-white font-medium shadow-[0_10px_44px_-10px_rgba(99,102,241,0.85)] hover:shadow-[0_12px_52px_-8px_rgba(99,102,241,1)] transition-shadow border-0"
              />
              <Button
                size="lg"
                variant="outline"
                onClick={scrollToExperience}
                className="h-12 px-7 bg-white/[0.04] border-white/15 text-white hover:bg-white/[0.08] hover:text-white backdrop-blur-md"
              >
                See the Learning Path
              </Button>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-white/55">
              {[
                ["bg-emerald-400", "Plans from $24.99/mo"],
                ["bg-violet-400", "Parent-managed"],
                ["bg-cyan-400", "Ages 13+"],
                ["bg-amber-300", "Parent-managed households"],
              ].map(([dot, label]) => (
                <span key={label} className="inline-flex items-center gap-2">
                  <span className={`h-1 w-1 rounded-full ${dot}`} />
                  {label}
                </span>
              ))}
            </div>
          </div>

          {/* Right: interactive guide orbit + floating cards (desktop) */}
          <div className="relative hidden lg:block">
            <div className="relative mx-auto aspect-square max-w-[520px]">
              {/* Portal ring */}
              <div aria-hidden className="absolute inset-6 rounded-full border border-white/10" />
              <div aria-hidden className="absolute inset-12 rounded-full border border-white/[0.06]" />
              <div aria-hidden className="absolute inset-0 rounded-full bg-[conic-gradient(from_0deg,transparent,rgba(34,211,238,0.35),transparent_30%,rgba(167,139,250,0.35),transparent_60%,rgba(251,191,36,0.25),transparent_90%)] blur-[2px] opacity-60 animate-[spin_28s_linear_infinite]" />
              <div aria-hidden className="absolute inset-[18%] rounded-full bg-[radial-gradient(circle,rgba(99,102,241,0.45),transparent_70%)] blur-2xl" />

              {/* Lumi center crest (interactive) */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                {renderGuideButton(lumi, "h-32 w-32 sm:h-36 sm:w-36", "bottom")}
                <div className="mt-2 text-center text-[10px] uppercase tracking-[0.3em] text-indigo-200/90">Lumi</div>
              </div>

              {/* Orbiting interactive guide portraits */}
              {guides.map((g, i) => {
                const placement = i === 0 ? "bottom" : i === 1 ? "right" : "left";
                return (
                  <div key={g.id} className={`absolute ${g.pos} z-30`}>
                    {renderGuideButton(g, "h-20 w-20 sm:h-24 sm:w-24", placement)}
                    <div className="mt-1.5 text-center text-[9px] uppercase tracking-[0.24em] text-white/75">{g.name}</div>
                  </div>
                );
              })}

              {/* Floating decision card */}
              <div className="absolute -left-6 sm:-left-10 top-[42%] z-10 w-[200px] rounded-2xl border border-cyan-300/30 bg-[#11102a]/90 backdrop-blur-xl p-3.5 shadow-[0_18px_60px_-20px_rgba(34,211,238,0.45)]">
                <div className="text-[9px] uppercase tracking-[0.26em] text-cyan-300/90 flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3" /> Decide
                </div>
                <div className="mt-1.5 text-[12px] text-white leading-snug font-medium">
                  Friend wants to split a $480 concert pass. Your phone bill is due.
                </div>
                <div className="mt-2 rounded-md border border-cyan-300/40 bg-cyan-400/10 px-2 py-1 text-[11px] text-white">
                  Decline · protect the bill
                </div>
              </div>

              {/* Floating parent insight card */}
              <div className="absolute -right-4 sm:-right-8 top-[14%] z-10 w-[200px] rounded-2xl border border-violet-300/30 bg-[#11102a]/90 backdrop-blur-xl p-3.5 shadow-[0_18px_60px_-20px_rgba(167,139,250,0.45)]">
                <div className="text-[9px] uppercase tracking-[0.26em] text-violet-300/90 flex items-center gap-1.5">
                  <TrendingUp className="h-3 w-3" /> Parent Insight
                </div>
                <div className="mt-1.5 text-[12px] text-white font-medium">Confidence ↑ in Cash Flow</div>
                <div className="text-[10px] text-white/60 mt-0.5">Worth a 2-minute talk</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;